import { fetchUserInfo, fetchCompanyApply } from '@/api/user'
import cloneDeep from 'lodash/cloneDeep'
import { getLanguage } from '@/i18n'

// 身份信息唯一加载器：60秒缓存，显式force刷新，合并并发请求。
const IDENTITY_CACHE_MS = 60000
let identityPending = null
let identityGeneration = 0
const withTimeout = task => {
  let timer
  return Promise.race([task, new Promise((resolve, reject) => {
    timer = setTimeout(() => reject(new Error('企业状态请求超时')), 15000)
  })]).finally(() => clearTimeout(timer))
}
const state = {
  identityPhase: 'LOADING',
  identityLoadedAt: 0,
  // 是否登陆
  isLogin: false,
  // 是否加入企业
  hasCompany: false,
  // 产品开通状态
  openedProducts: {},
  // 产品开启状态
  openedStatus: {},
  // 用户信息数据
  userInfo: {},
  // 当前公司信息
  companyInfo: {},
  // 当前页面路由名称
  currentTab: null,
  // 当前页面路由地址
  currentHref: null,
  // 子组件名称，用于判断是否调用消息接口
  childName: '',
  // 站内消息数量
  noticeCount: 0,
  language: getLanguage()
}

const mutations = {
  setIdentityLoad(state, { phase, loadedAt = 0 }) {
    state.identityPhase = phase
    state.identityLoadedAt = loadedAt
  },
  // 修改用户数据
  setUserInfo(state, data) {
    state.userInfo = cloneDeep(data)
    state.isLogin = data && data.userId ? true : false
    state.hasCompany = data && data.companyStatus === 20
  },

  // 修改当前公司信息
  setCompanyInfo(state, data) {
    state.companyInfo = cloneDeep(data)
  },

  getUserInfo() {
    return state.userInfo
  },

  getCompanyInfo() {
    return state.companyInfo
  },

  // 修改登陆状态
  setIsLogin(state, data) {
    state.isLogin = data
  },

  // 修改加入企业状态
  setHasCompany(state, data) {
    state.hasCompany = data
  },

  // 设置产品开通状态
  setOpenedProducts(state, data) {
    state.openedProducts = data
  },

  // 设置产品开启状态
  setOpenedStatus(state, data) {
    state.openedStatus = data
  },

  setCurrentTab(state, data) {
    state.currentTab = cloneDeep(data)
  },

  setCurrentHref(state, data) {
    state.currentHref = cloneDeep(data)
  },

  setChildName(state, data) {
    state.childName = cloneDeep(data)
  },

  // 设置站内消息数量
  setNoticeCount(state, data) {
    state.noticeCount = cloneDeep(data)
  },

  setLanguage(state, data) {
    state.language = data
  }
}

const actions = {
  loadIdentity({ state, commit }, force = false) {
    if (identityPending) return identityPending
    // 非force仅复用有效的READY快照，过期或失败必须重新加载。
    if (!force && state.identityPhase === 'READY' && Date.now() - state.identityLoadedAt < IDENTITY_CACHE_MS) {
      return Promise.resolve()
    }
    const current = ++identityGeneration
    commit('setIdentityLoad', { phase: 'LOADING' })
    identityPending = (async () => {
      try {
        let userInfo = {}
        let companyInfo = {}
        if (localStorage.getItem('userToken')) {
          const user = await withTimeout(fetchUserInfo())
          if (!user.success || !user.data || !user.data.userId) throw new Error('获取用户状态失败')
          userInfo = user.data
          if (userInfo.companyCode) {
            const company = await withTimeout(fetchCompanyApply())
            if (!company.success || !company.data) throw new Error('获取企业状态失败')
            companyInfo = company.data
          }
        }
        if (current === identityGeneration) {
          commit('setUserInfo', userInfo)
          commit('setCompanyInfo', companyInfo)
          commit('setIdentityLoad', { phase: 'READY', loadedAt: Date.now() })
        }
      } catch (error) {
        if (current === identityGeneration) {
          commit('setUserInfo', {})
          commit('setCompanyInfo', {})
          commit('setIdentityLoad', { phase: 'LOAD_FAILED' })
        }
      }
    })().finally(() => { if (current === identityGeneration) identityPending = null })
    return identityPending
  },
  // 退出登录
  logout({ commit, state, dispatch }) {
    return new Promise((resolve, reject) => {
      // commit('setToken', '')
      // 清除用户数据
      commit('setUserInfo', {})
      // 清除当前公司信息
      commit('setCompanyInfo', {})
      // 设置登录状态为false
      commit('setIsLogin', false)
      identityGeneration++
      identityPending = null
      commit('setIdentityLoad', { phase: 'READY', loadedAt: Date.now() })

      resolve()
    })
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
